import React, { ReactElement, ReactNode, useEffect, useMemo, useState } from 'react';
import { debounce } from '@app/utils/helpers/debounce';
import {
  SprintProductEntity,
  SprintProductParamsType,
  SprintProductStatus,
  useCreateSprintProduct,
  useListSprintProductClients,
  useUpdateSprintProduct,
} from '@data';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { Button } from '@ui/components/ui/button';
import { Label } from '@ui/components/ui/label';
import { Textarea } from '@ui/components/ui/textarea';
import { cn } from '@ui/lib/utils';
import { ReportNoteBox } from '@app/components/core';
import ExpandableContent from '@app/components/shared/expandableContent';
import { Controller, useForm } from 'react-hook-form';
import { Sparkles } from 'lucide-react';
import { useAISummary, useOrganizationId } from '@app/utils/hooks';

export interface SummaryProps extends React.HTMLAttributes<HTMLDivElement> {
  sprintProduct: SprintProductEntity | undefined;
  /** Whatever stands beside the field, sharing its row: today, the remark a dispute was made of. */
  note?: ReactNode;
}

const Summary = ({ sprintProduct, note }: SummaryProps): ReactElement => {
  const organizationId = useOrganizationId() as string;
  const { productId, sprintId } = useParams() as Pick<SprintProductParamsType, 'sprintId' | 'productId'>;
  const { t } = useTranslation();
  const [summary, setSummary] = useState<string | undefined>(sprintProduct?.summary);

  const { mutateAsync: createSprintProduct, isPending: isCreateLoading } = useCreateSprintProduct(
    organizationId,
    {},
    { sprintId, productId },
  );
  const { mutate: updateSprintProduct } = useUpdateSprintProduct(organizationId, {}, { sprintId, productId });
  const { data: sprintProductClients } = useListSprintProductClients(
    sprintProduct && !sprintProduct.mutation ? sprintProduct.id : undefined,
  );

  const { aiSummary, generateAISummary, isAISummaryGenerating } = useAISummary();

  const update = (values: Partial<SprintProductEntity>) => {
    updateSprintProduct({ ...sprintProduct, ...values });
  };

  const debounceUpdate = useMemo(() => debounce(update, 500), [sprintProduct]);

  const initialValues = useMemo(
    () => ({
      summary: sprintProduct?.summary,
    }),
    [sprintProduct],
  );

  const { control, handleSubmit } = useForm({
    defaultValues: initialValues,
  });

  useEffect(() => {
    if (!sprintProduct) {
      createSprintProduct({ productId, sprintId, status: SprintProductStatus.open });
    }
  }, []);

  useEffect(() => {
    if (sprintProduct && summary !== sprintProduct.summary && !isCreateLoading && !isAISummaryGenerating) {
      debounceUpdate({ summary });
    }
  }, [summary, isAISummaryGenerating]);

  useEffect(() => {
    if (aiSummary && isAISummaryGenerating) {
      setSummary(aiSummary);
    }
  }, [aiSummary, isAISummaryGenerating]);

  const handleGenerateAISummary = (sprintProductId?: string) => () =>
    sprintProductId && generateAISummary(sprintProductId);

  const isApproved =
    sprintProduct?.status === SprintProductStatus.approved || sprintProduct?.status === SprintProductStatus.invoiced;

  /**
   * A report that has gone to a client is settled — what they were sent is not ours to quietly rewrite —
   * until we deliberately open it again, which is how a remark gets answered. Editing is safe there
   * because the client keeps reading the version we published; they see this one when we close it.
   */
  const isSettled = !!sprintProductClients?.items.length && sprintProduct?.status !== SprintProductStatus.open;

  return (
    <form onSubmit={handleSubmit(update)}>
      {/* The field and the note beside it stretch to whichever is taller, so the two end on the same line.
          The generate button waits below the row instead of inside the field's own column, which would
          stretch the note past the field it is standing beside. */}
      <div className="mb-3 flex flex-wrap items-stretch gap-4">
        {isSettled ? (
          // Titled inside its own box once there is nothing to type into: the same box the client reads
          // this summary in, which is also what puts its title level with the remark's rather than above it.
          <ReportNoteBox title={t('sprintSummary')}>
            <ExpandableContent content={sprintProduct?.summary} />
          </ReportNoteBox>
        ) : (
          <Controller
            name="summary"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <div className="flex min-w-0 flex-1 basis-0 flex-col gap-1.5">
                <Label htmlFor="sprint-summary-field">{t('sprintSummary')}</Label>
                <Textarea
                  {...field}
                  id="sprint-summary-field"
                  rows={4}
                  value={summary ?? ''}
                  onChange={e => setSummary(e.target.value)}
                  aria-invalid={!!error}
                  // Only bites once the row has stretched this column past the field's own height, which is
                  // exactly when the note is the taller of the two. On its own the field still sizes to what
                  // has been typed in it.
                  className={cn('h-full min-h-28', error && 'border-destructive')}
                />
                {error?.message ? <p className="text-sm text-destructive">{error.message}</p> : null}
              </div>
            )}
          />
        )}
        {note}
      </div>

      {/* Under the field rather than above it: it fills that box, so it belongs to the summary rather than
          reading as an action on the page. Nothing left to summarise once the client has signed off, though:
          rewriting the summary of a report they already approved would change what they agreed to. */}
      {!isApproved && (
        <div className="mb-6">
          <Button
            variant="outline"
            key="getSummaryButton"
            onClick={handleGenerateAISummary(sprintProduct?.id)}
            disabled={isSettled || isAISummaryGenerating || isCreateLoading}
          >
            {aiSummary && !isAISummaryGenerating ? t('actions.regenerateSummaryAI') : t('actions.generateSummaryAI')}
            <Sparkles size={16} className="-ml-0.5" />
          </Button>
        </div>
      )}

      {/* The client's comment isn't repeated here: it belongs to their approval, which the status pill in
          the header opens in full — the same place it lives on their own view of this report. */}
    </form>
  );
};

export default Summary;
